import { useEffect, useMemo, useState } from "react";
import { Box, Chip, Dialog, DialogContent, IconButton, Skeleton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { masterApi } from "../../../apis/masterApi";
import { encodeData } from "../../../helper/common/encodeData";
import FilterPreview from "../../StopLossList/subComponents/FilterViewer/FilterViewer";
import { CAMPAIGN_FETCH_LIMIT } from "../lib/constants";
import {
  buildSelectedCampaignsApiFilter,
  getCampaignDisplayName,
  getCampaignMongoId,
  isValidCampaignListItem,
} from "../lib/helpers";
import { BID_V2_COLORS } from "../lib/bidAutomationStyles";

const ACTION_STYLE = {
  INCREASE_BID: { label: "Increase bid", bg: "rgba(22, 163, 74, 0.12)", color: "#15803D" },
  DECREASE_BID: { label: "Decrease bid", bg: "rgba(220, 38, 38, 0.1)", color: "#B91C1C" },
  PAUSE: { label: "Pause targeting", bg: "rgba(217, 119, 6, 0.14)", color: "#B45309" },
  UPDATE_BID: { label: "Update bid", bg: "rgba(97, 72, 246, 0.12)", color: "#5B21B6" },
};

function pick(source, ...keys) {
  if (!source || typeof source !== "object") return "";
  for (const key of keys) {
    const value = source[key];
    if (value != null && value !== "") return value;
  }
  return "";
}

function formatDate(value) {
  const match = String(value ?? "").match(/^(\d{4}-\d{2}-\d{2})/);
  if (!match) return value ? String(value) : "—";
  const date = new Date(`${match[1]}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function entityFilters(rule) {
  const filters =
    rule?.entityFilter?.entityFilterRule?.filters ?? rule?.entity_filter?.entity_filter_rule?.filters ?? [];
  return Array.isArray(filters) ? filters : [];
}

function campaignIdsFromRule(rule) {
  return [
    ...new Set(
      entityFilters(rule)
        .filter((filter) => (filter?.field || filter?.field_name) === "Campaign_Id")
        .flatMap((filter) =>
          Array.isArray(filter?.value)
            ? filter.value.map(String)
            : filter?.value != null
              ? [String(filter.value)]
              : []
        )
        .filter(Boolean)
    ),
  ];
}

function ruleTasks(rule) {
  const tasks = rule?.ruleTasks ?? rule?.rule_tasks ?? [];
  return Array.isArray(tasks) ? tasks : [];
}

function cubeQueryFilters(metric) {
  const raw = metric?.cubeFilters ?? metric?.cube_filters;
  if (!raw) return null;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return parsed?.query?.filters ?? null;
  } catch {
    return null;
  }
}

function actionSummary(action) {
  const type = action?.actionType ?? action?.action_type ?? "";
  const style = ACTION_STYLE[type] || {
    label: type ? String(type).replace(/_/g, " ") : "Action",
    bg: "rgba(100, 116, 139, 0.12)",
    color: "#334155",
  };
  const change = action?.percentageChange ?? action?.percentage_change;
  const withChange =
    change != null && change !== "" && type !== "PAUSE"
      ? `${style.label} · ${String(change).includes("%") ? change : `${change}%`}`
      : style.label;
  return { ...style, text: withChange };
}

function MetaItem({ label, value }) {
  return (
    <Box
      sx={{
        flex: "1 1 140px",
        minWidth: 140,
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        bgcolor: "#fff",
        border: `1px solid ${BID_V2_COLORS.border}`,
      }}
    >
      <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.04em", color: BID_V2_COLORS.muted }}>
        {label}
      </Typography>
      <Typography sx={{ mt: 0.25, fontSize: "0.875rem", fontWeight: 600, color: BID_V2_COLORS.title }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default function BidAutomationLogConfigDialog({ open, snapshot, source, onClose }) {
  const rule = snapshot && typeof snapshot === "object" ? snapshot : {};
  const schedule = pick(rule, "scheduleConfig", "schedule_config") || {};
  const tasks = ruleTasks(rule);
  const campaignIds = useMemo(() => (open ? campaignIdsFromRule(rule) : []), [open, rule]);
  const campaignIdsKey = campaignIds.join(",");
  const [campaignNames, setCampaignNames] = useState({});
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const status = pick(rule, "status");
  const modifiedBy = pick(rule, "modifiedBy", "modified_by");

  useEffect(() => {
    if (!open) return undefined;
    const ids = campaignIdsKey ? campaignIdsKey.split(",") : [];
    if (!ids.length) {
      setCampaignNames({});
      setCampaignsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadNames = async () => {
      setCampaignsLoading(true);
      try {
        const chunks = [];
        for (let i = 0; i < ids.length; i += CAMPAIGN_FETCH_LIMIT) {
          chunks.push(ids.slice(i, i + CAMPAIGN_FETCH_LIMIT));
        }
        const responses = await Promise.all(
          chunks.map((chunk) =>
            masterApi(
              {
                source,
                limit: chunk.length,
                order_by: "daily_budget",
                sorting_order: "DESC",
                filtering: encodeData(buildSelectedCampaignsApiFilter(chunk)),
              },
              "campaignList",
              false
            )
          )
        );
        if (cancelled) return;
        const names = {};
        responses
          .flatMap((result) => result?.data?.data ?? [])
          .filter(isValidCampaignListItem)
          .forEach((campaign) => {
            const id = getCampaignMongoId(campaign);
            if (id) names[id] = getCampaignDisplayName(campaign);
          });
        setCampaignNames(names);
      } catch {
        if (!cancelled) setCampaignNames({});
      } finally {
        if (!cancelled) setCampaignsLoading(false);
      }
    };

    loadNames();
    return () => {
      cancelled = true;
    };
  }, [open, campaignIdsKey, source]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 1600 }}
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.25,
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          background: "linear-gradient(180deg, #F6F3FF 0%, #FFFFFF 100%)",
          borderBottom: `1px solid ${BID_V2_COLORS.border}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: BID_V2_COLORS.title, lineHeight: 1.3 }}>
            {pick(rule, "name") || "Rule config"}
          </Typography>
          <Typography sx={{ mt: 0.4, fontSize: "0.8125rem", color: BID_V2_COLORS.muted }}>
            Config saved on this log run
          </Typography>
        </Box>
        {status ? (
          <Chip
            size="small"
            label={status}
            sx={{ mt: 0.25, fontWeight: 700, bgcolor: "rgba(97, 72, 246, 0.12)", color: "#5B21B6" }}
          />
        ) : null}
        <IconButton aria-label="Close config" onClick={onClose} size="small" sx={{ mt: -0.25 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: "#F7F6FB", px: 3, py: 2.5 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 2.5 }}>
          <MetaItem label="START" value={formatDate(pick(schedule, "startDate", "start_date"))} />
          <MetaItem label="END" value={formatDate(pick(schedule, "endDate", "end_date"))} />
          <MetaItem label="MODIFIED BY" value={modifiedBy || "—"} />
        </Box>

        <Typography sx={{ mb: 1, fontSize: "0.8125rem", fontWeight: 700, color: BID_V2_COLORS.title }}>
          Campaigns
        </Typography>
        <Box
          sx={{
            mb: 2.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "#fff",
            border: `1px solid ${BID_V2_COLORS.border}`,
          }}
        >
          {campaignIds.length ? (
            <>
              <Typography sx={{ mb: 1, fontSize: "0.75rem", fontWeight: 600, color: BID_V2_COLORS.muted }}>
                {campaignIds.length} selected
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, maxHeight: 148, overflow: "auto" }}>
                {campaignsLoading
                  ? campaignIds.slice(0, 6).map((id) => (
                      <Skeleton key={id} variant="rounded" width={140} height={24} />
                    ))
                  : campaignIds.map((id) => (
                      <Chip
                        key={id}
                        size="small"
                        label={campaignNames[id] || id}
                        sx={{ fontSize: "0.75rem", bgcolor: "#F4F2FF", color: BID_V2_COLORS.title }}
                      />
                    ))}
              </Box>
            </>
          ) : (
            <Typography sx={{ fontSize: "0.8125rem", color: BID_V2_COLORS.muted }}>No campaigns</Typography>
          )}
        </Box>

        <Typography sx={{ mb: 1, fontSize: "0.8125rem", fontWeight: 700, color: BID_V2_COLORS.title }}>
          Tasks
        </Typography>
        {tasks.length ? (
          tasks.map((task, index) => {
            const action =
              task?.ruleActionConfig?.entityActionConfig ?? task?.rule_action_config?.entity_action_config ?? {};
            const metric = task?.entityMetricFilter ?? task?.entity_metric_filter ?? {};
            const summary = actionSummary(action);
            const filtersForPreview = cubeQueryFilters(metric);
            return (
              <Box
                key={`${task?.ruleTaskName || task?.rule_task_name || "task"}-${index}`}
                sx={{
                  mb: 1.5,
                  p: 1.75,
                  borderRadius: 2,
                  bgcolor: "#fff",
                  border: `1px solid ${BID_V2_COLORS.border}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.25 }}>
                  <Typography sx={{ fontWeight: 700, color: BID_V2_COLORS.title, fontSize: "0.9375rem" }}>
                    {task?.ruleTaskName || task?.rule_task_name || `Task ${index + 1}`}
                  </Typography>
                  <Chip
                    size="small"
                    label={summary.text}
                    sx={{ fontWeight: 700, bgcolor: summary.bg, color: summary.color }}
                  />
                </Box>
                <FilterPreview filters={filtersForPreview} />
              </Box>
            );
          })
        ) : (
          <Typography sx={{ fontSize: "0.8125rem", color: BID_V2_COLORS.muted }}>No tasks</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
